import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

/**
 * Shared fetch shell for the Pro History drawer sub-panels.
 *
 * Guards against stale responses: when `url` changes while a request is in
 * flight (masterpro prev/next navigation keeps the panels mounted), the
 * superseded request's response is discarded and the previous PRO's rows are
 * cleared immediately rather than lingering under the new PRO's header. A
 * refreshNonce bump refetches without blanking the table.
 */
export function useHistoryPanelData<T>(
  url: string | null,
  active: boolean,
  refreshNonce: number,
  errorMessage: string
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !url) return;
    if (lastUrlRef.current !== url) {
      lastUrlRef.current = url;
      setData(null);
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<T>(url)
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || errorMessage);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, url, refreshNonce, errorMessage]);

  return { data, loading, error };
}

export default useHistoryPanelData;
